import { useMemo } from 'react'
import { useGetSettings } from '@/api/generated/setting/setting'
import type { ApplicationSettingsPerStateAdminApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { getStateByAbbreviation } from '@/utils/common'

type StateSettingWithName = ApplicationSettingsPerStateAdminApiContract & { StateName: string }

/**
 * Shared per-state settings read.
 *
 * Replaces the management `getSettingsPerState` thunk for the States page + edit-state flow.
 * Reproduces the old GET_STATES_SETTINGS_SUCCESS reducer decoration: it tags each entry with
 * a derived `StateName` (from getStateByAbbreviation) and returns the list sorted alphabetically
 * by that name — the exact ordering the old `management.statesSettings.SettingsPerState` exposed.
 *
 * Note: the wider `management.statesSettings` Redux field stays in place — it is still written
 * by the drugRules boot fetch (GET_DRUG_RULES_SETTINGS_SUCCESS) and read by NewVisit / FilesSection,
 * so only the States page + edit flow move to this hook.
 */
export const useStateSettings = () => {
  const query = useGetSettings()

  const settingsPerState = useMemo<StateSettingWithName[]>(() => {
    const list = query.data?.SettingsPerState ?? []
    return list
      .map((setting) => ({
        ...setting,
        StateName: getStateByAbbreviation(setting.StateCode ?? '') ?? '',
      }))
      .sort((a, b) => a.StateName.localeCompare(b.StateName))
  }, [query.data])

  return { ...query, settingsPerState }
}
